import { useEffect, useState } from 'react';
import { ApiAuth, LoginPayload, RouteEnum, userApi, VerificationPayload } from 'App';
import { useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { getInitialPage } from '@shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { currentUserSelector, initialUser } from './Session.utils';
import { ContextValues, SessionStatus } from './Session.types';

export function useSessionProvider() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [status, setStatus] = useState<SessionStatus>(SessionStatus.Initializing);
  const [isCredsEntered, setCredsEntered] = useState(false);
  const [isUserRefreshedPageOnVerification, setUserRefreshedPageOnVerification] = useState(false);
  const [currentUser, setCurrentUser] = useState<ContextValues['currentUser']>(initialUser);
  const initialPage = getInitialPage(currentUser);

  const authenticate = (doNavigate: boolean, user?: ContextValues['currentUser']) => {
    setStatus(SessionStatus.Authenticated);
    if (doNavigate) {
      navigate(user ? getInitialPage(user) : initialPage);
    }
  };
  const unauthenticate = (doNavigateToLogin = true, doNavigateToVerification = false) => {
    setStatus(SessionStatus.Unauthenticated);
    setCurrentUser(initialUser);
    queryClient.clear();
    if (doNavigateToLogin) {
      navigate(RouteEnum.Login);
    }
    if (doNavigateToVerification) {
      navigate(RouteEnum.Verification);
    }
  };

  const login = async ({ email, password }: LoginPayload) => {
    try {
      const { roles, partnerUUIDs } = await ApiAuth.login({ email, password });
      queryClient.setQueryData(['user roles'], () => ({
        partnerUUIDs,
        roles,
      }));
      setCredsEntered(true);
      setCurrentUser(currentUserSelector({ partnerUUIDs, roles }));
      navigate(RouteEnum.Verification);
    } catch (error) {
      if (isAxiosError(error)) {
        return error.response?.data.ErrorMessage;
      } else {
        console.error(error);
      }
    }
  };
  const verification = async ({ code }: VerificationPayload) => {
    try {
      await ApiAuth.verification({ code });
      if (!queryClient.getQueryData(['user roles'])) {
        // if user refreshed page between login and verification (roles from login request cleared)
        setUserRefreshedPageOnVerification(true);
        refetch();
      } else {
        authenticate(true);
      }
    } catch (error) {
      if (isAxiosError(error)) {
        console.error(error.response?.data.ErrorMessage);
        return error.response?.data.ErrorMessage;
      } else {
        console.error(error);
      }
    }
  };
  const logout = async () => {
    try {
      await ApiAuth.logout();
      unauthenticate();
      setCredsEntered(false);
    } catch (error) {
      console.error(error);
    }
  };

  const { refetch } = useQuery(['user roles'], userApi.getUserRoles, {
    enabled: false,
    select: (data) => currentUserSelector(data),
    onSuccess: (user) => {
      setCredsEntered(true);
      if (user.verified) {
        setCurrentUser(user);
        if (isUserRefreshedPageOnVerification) {
          authenticate(true, user);
        } else {
          authenticate(false);
        }
      } else {
        unauthenticate(false, true);
      }
    },
    onError: () => unauthenticate(false),
  });

  useEffect(() => {
    refetch();
  }, [refetch]);

  function inStatus(s: SessionStatus) {
    return status === s;
  }

  return {
    initializing: inStatus(SessionStatus.Initializing),
    authenticated: inStatus(SessionStatus.Authenticated),
    unauthenticated: inStatus(SessionStatus.Unauthenticated),
    unauthenticate,
    login,
    verification,
    logout,
    currentUser,
    initialPage,
    isCredsEntered,
    status,
  };
}
